<script setup lang="ts">
/**
 * Pilule « Propulsé par Donoor.org » des tunnels embarqués (boutique ; prête
 * pour billetterie et adhésion) — EN BAS du widget, centrée.
 *
 * Décision Karim 31/08 : dans le widget (« intégrer à l'embed, c'est plus
 * logique ») et en bas (« sur la ligne du titre, c'est trop voyant »). Refusés
 * le même jour : dans la page hôte à côté du titre de l'asso, et sur la ligne
 * « Les produits ».
 *
 * Même dessin que le badge du formulaire de don (`DonateEmbedApp`) : pilule
 * bleu nuit pleine, D blanc au cœur bleu nuit, « Donoor » en jaune marque.
 * L'appel à l'action (« Créez la boutique de votre association ») ne s'affiche
 * que quand le widget est large (`wide:`, cf. main.css) : à côté du panier,
 * dans une colonne étroite, il n'y a pas la place.
 *
 * Le lien mène à la page produit de l'OUTIL embarqué, avec un utm qui trace
 * l'origine (type + site hôte). Ouverture dans un NOUVEL onglet forcée via
 * `window.open` : depuis une iframe cross-origin, `target=_blank` seul est
 * parfois bridé.
 *
 * ⚠️ SEO : ce lien vit DANS l'iframe (page noindex) — il n'apporte aucun
 * backlink. Le backlink, c'est le bouton `<a href>` posé par embed.js dans le
 * HTML du site hôte (modes popup / flottant).
 */
const props = defineProps<{
  type: "shop" | "event" | "adhesion";
  /** Origine du site hôte (`?host=`), pour le `ref` de l'utm. */
  hostOrigin?: string | null;
}>();

// `cta` = clé i18n, traduite au rendu.
const LANDING: Record<typeof props.type, { path: string; cta: string }> = {
  shop: { path: "/boutique", cta: "partage.propulsePar.ctaBoutique" },
  event: { path: "/billetterie", cta: "partage.propulsePar.ctaBilletterie" },
  adhesion: { path: "/adhesion", cta: "partage.propulsePar.ctaAdhesion" },
};

const landing = computed(() => LANDING[props.type]);

const href = computed(() => {
  const params = new URLSearchParams({
    utm_source: "embed",
    utm_medium: "widget",
    utm_campaign: "powered-by",
    utm_content: props.type,
  });
  const host = props.hostOrigin ? safeHostname(props.hostOrigin) : null;
  if (host) params.set("ref", host);
  return `https://donoor.org${landing.value.path}?${params.toString()}`;
});

function safeHostname(origin: string): string | null {
  try {
    return new URL(origin).hostname || null;
  } catch {
    return null;
  }
}

function open(e: MouseEvent) {
  e.preventDefault();
  window.open(href.value, "_blank", "noopener,noreferrer");
}
</script>

<template>
  <a
    :href="href"
    target="_blank"
    rel="noopener noreferrer"
    class="embed-powered-by inline-flex items-center gap-2 h-10 pl-2.5 pr-3.5 rounded-full bg-[#040a32] text-[13px] leading-none text-white no-underline shrink-0 transition-[transform,box-shadow] duration-150 hover:-translate-y-px"
    :title="$t('partage.propulsePar.titre', { cta: $t(landing.cta) })"
    @click="open"
  >
    <svg
      width="18"
      height="18"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      class="shrink-0"
    >
      <path
        d="M12 10 C12 7.79 13.79 6 16 6 L36 6 C50 6 58 16 58 32 C58 48 50 58 36 58 L16 58 C13.79 58 12 56.21 12 54 Z"
        fill="#ffffff"
      />
      <path
        d="M28 29 C26 27 23 27 23 30 C23 33 28 37 28 37 C28 37 33 33 33 30 C33 27 30 27 28 29 Z"
        fill="#040a32"
      />
    </svg>
    <span class="whitespace-nowrap text-[#ffcf01]">{{ $t("partage.propulsePar.propulsePar") }} <b class="font-bold">Donoor.org</b></span>
    <span aria-hidden="true" class="hidden wide:block w-px h-3.5 bg-white/25 shrink-0" />
    <span class="hidden wide:inline whitespace-nowrap font-semibold">{{ $t(landing.cta) }}</span>
  </a>
</template>

<style scoped>
.embed-powered-by {
  box-shadow: 0 2px 8px rgba(4, 10, 50, 0.25);
}
.embed-powered-by:hover {
  box-shadow: 0 4px 14px rgba(4, 10, 50, 0.4);
}
</style>
